import { useState, useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { usePaginatedData, PaginatedResponse } from "@/hooks/usePaginatedData";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Download,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Loader2,
  ScrollText,
  Eye,
  Bot,
  Clock,
  Coins,
  Zap,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { isSuperAdmin } from "@/lib/roles";
import { usePermissions } from "@/hooks/usePermissions";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { useSortableData } from "@/hooks/useSortableData";
import { automationApi } from "@/services/api/automation";
import { toast } from "sonner";

export const Route = createFileRoute("/portal/automation/audit")({
  component: AuditLogs,
});

interface AIActionLog {
  id: string;
  entity_id: string;
  entity_type: string;
  agent_name: string;
  action_type: string;
  service_name: string;
  status: string;
  total_tokens: number;
  cost_estimate: number;
  execution_time_ms: number;
  workflow_execution_id: string | null;
  created_at: string;
  timestamp: string;
}

function AuditLogs() {
  const { token, session } = useAuth();
  const { can } = usePermissions();
  const { selectedOrganisation } = usePortalData();
  const organisationId = selectedOrganisation?.organisationId;
  const showCosts = isSuperAdmin(session);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [entityTypeFilter, setEntityTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [agentFilter, setAgentFilter] = useState<string>("all");

  // For triggering search on button click
  const [appliedSearch, setAppliedSearch] = useState("");

  // Detail dialog
  const [selectedLog, setSelectedLog] = useState<AIActionLog | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [logDetails, setLogDetails] = useState<Record<string, unknown> | null>(
    null,
  );

  // Fetch function for pagination
  const fetchLogs = useCallback(
    async (
      page: number,
      pageSize: number,
    ): Promise<PaginatedResponse<AIActionLog>> => {
      if (!token) {
        return { data: [], totalCount: 0, page, pageSize };
      }
      try {
        const params: Record<string, string> = {
          limit: String(pageSize),
          offset: String((page - 1) * pageSize),
        };

        if (entityTypeFilter !== "all") {
          params.entity_type = entityTypeFilter;
        }

        const response = await automationApi.audit.listLogs(token, params);

        let logs = response.logs as AIActionLog[];

        // Client-side filtering for search and status (API may not support all filters)
        if (appliedSearch) {
          const query = appliedSearch.toLowerCase();
          logs = logs.filter(
            (log) =>
              log.entity_id.toLowerCase().includes(query) ||
              log.agent_name.toLowerCase().includes(query) ||
              log.action_type.toLowerCase().includes(query) ||
              (log.workflow_execution_id?.toLowerCase().includes(query) ??
                false),
          );
        }

        if (statusFilter !== "all") {
          logs = logs.filter((log) => log.status === statusFilter);
        }

        if (agentFilter !== "all") {
          logs = logs.filter((log) => log.agent_name === agentFilter);
        }

        return {
          data: logs,
          totalCount: response.pagination.total,
          page,
          pageSize,
        };
      } catch (error) {
        console.error("Failed to load AI action logs:", error);
        toast.error("Failed to load AI action logs");
        return { data: [], totalCount: 0, page, pageSize };
      }
    },
    [token, entityTypeFilter, statusFilter, agentFilter, appliedSearch],
  );

  const {
    data: logs,
    isLoading,
    pagination,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<AIActionLog>({
    fetchFn: fetchLogs,
    initialPageSize: 25,
    dependencies: [
      token,
      organisationId,
      entityTypeFilter,
      statusFilter,
      agentFilter,
      appliedSearch,
    ],
  });

  // Client-side sort on the current page of data
  const { sortedData: sortedLogs, requestSort, getSortDirection } =
    useSortableData(logs, {
      initialSort: { key: "timestamp", direction: "desc" },
    });

  // Check view permission
  if (!can("view", "automation")) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="py-12 text-center">
            <ScrollText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">Access Restricted</h3>
            <p className="text-muted-foreground mt-2">
              You don't have permission to view audit logs.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Get unique agents from current logs for filter dropdown
  const uniqueAgents = [...new Set(logs.map((log) => log.agent_name))].sort();

  const handleSearch = () => {
    setAppliedSearch(searchQuery);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setAppliedSearch("");
    setEntityTypeFilter("all");
    setStatusFilter("all");
    setAgentFilter("all");
  };

  const loadLogDetails = async (log: AIActionLog) => {
    setSelectedLog(log);
    setDetailsLoading(true);
    setLogDetails(null);

    try {
      // Try to get detailed audit trail if available
      if (log.workflow_execution_id && token) {
        const details = await automationApi.audit.getWorkflowDetails(
          token,
          log.workflow_execution_id,
        );
        setLogDetails(details as Record<string, unknown>);
      } else {
        // Just show the log itself
        setLogDetails(log as unknown as Record<string, unknown>);
      }
    } catch (error) {
      console.error("Failed to load log details:", error);
      setLogDetails(log as unknown as Record<string, unknown>);
    } finally {
      setDetailsLoading(false);
    }
  };

  const exportLogs = () => {
    const dataStr = JSON.stringify(logs, null, 2);
    const dataUri =
      "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
    const exportFileDefaultName = `ai-action-logs-${new Date().toISOString()}.json`;

    const linkElement = document.createElement("a");
    linkElement.setAttribute("href", dataUri);
    linkElement.setAttribute("download", exportFileDefaultName);
    linkElement.click();
  };

  const getStatusBadge = (status: string) => {
    if (status === "success") {
      return (
        <Badge variant="success">
          <CheckCircle className="mr-1 h-3 w-3" />
          Success
        </Badge>
      );
    }
    return (
      <Badge variant="destructive">
        <AlertCircle className="mr-1 h-3 w-3" />
        Error
      </Badge>
    );
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">AI Action Audit</h1>
          <p className="mt-2 text-lg text-slate-600">
            Complete audit trail of all AI actions for debugging and compliance
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={exportLogs}
            disabled={logs.length === 0}
            className="rounded-full"
          >
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Logs Table */}
      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => { setSearchQuery(""); setAppliedSearch(""); }}
            onKeyDown={handleKeyPress}
            placeholder="Search entity ID, agent, workflow ID..."
            containerClassName="flex-1 min-w-[200px]"
          />
          <Select value={entityTypeFilter} onValueChange={setEntityTypeFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="ticket">Ticket</SelectItem>
              <SelectItem value="document">Document</SelectItem>
              <SelectItem value="notification">Notification</SelectItem>
              <SelectItem value="workflow">Workflow</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="success">Success</SelectItem>
              <SelectItem value="error">Error</SelectItem>
            </SelectContent>
          </Select>
          <Select value={agentFilter} onValueChange={setAgentFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="All Agents" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Agents</SelectItem>
              {uniqueAgents.map((agent) => (
                <SelectItem key={agent} value={agent}>
                  {agent}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={handleSearch}>
            Search
          </Button>
          {(appliedSearch || entityTypeFilter !== "all" || statusFilter !== "all" || agentFilter !== "all") && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear filters
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={refresh} disabled={isLoading}>
            {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>

        {isLoading && logs.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12">
            <ScrollText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No AI action logs found</h3>
            <p className="text-muted-foreground">
              Logs will appear here once AI actions are executed.
            </p>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead direction={getSortDirection("timestamp")} onSort={() => requestSort("timestamp")}>Timestamp</SortableTableHead>
                  <SortableTableHead direction={null} onSort={() => {}} disabled>Entity</SortableTableHead>
                  <SortableTableHead direction={getSortDirection("agent_name")} onSort={() => requestSort("agent_name")}>Agent</SortableTableHead>
                  <SortableTableHead direction={getSortDirection("action_type")} onSort={() => requestSort("action_type")}>Action</SortableTableHead>
                  <SortableTableHead direction={getSortDirection("status")} onSort={() => requestSort("status")}>Status</SortableTableHead>
                  <SortableTableHead direction={getSortDirection("total_tokens")} onSort={() => requestSort("total_tokens")} className="text-right">Tokens</SortableTableHead>
                  {showCosts && <SortableTableHead direction={getSortDirection("cost_estimate")} onSort={() => requestSort("cost_estimate")} className="text-right">Cost</SortableTableHead>}
                  <SortableTableHead direction={getSortDirection("execution_time_ms")} onSort={() => requestSort("execution_time_ms")} className="text-right">Duration</SortableTableHead>
                  <SortableTableHead direction={null} onSort={() => {}} disabled className="w-[50px]">{" "}</SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-sm whitespace-nowrap">
                        {formatTimestamp(log.timestamp || log.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <Badge variant="outline" className="w-fit text-xs">
                            {log.entity_type}
                          </Badge>
                          <span className="text-xs text-muted-foreground font-mono mt-1">
                            {log.entity_id.length > 12
                              ? `${log.entity_id.substring(0, 12)}...`
                              : log.entity_id}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Bot className="h-3 w-3 text-muted-foreground" />
                          <span className="text-sm">{log.agent_name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {log.action_type}
                      </TableCell>
                      <TableCell>{getStatusBadge(log.status)}</TableCell>
                      <TableCell className="text-right text-sm">
                        {log.total_tokens.toLocaleString()}
                      </TableCell>
                      {showCosts && (
                        <TableCell className="text-right text-sm">
                          ${log.cost_estimate.toFixed(4)}
                        </TableCell>
                      )}
                      <TableCell className="text-right text-sm">
                        {formatDuration(log.execution_time_ms)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => loadLogDetails(log)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
            </Table>

            {/* Pagination */}
            <div className="mt-4 border-t pt-4">
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                totalCount={pagination.totalCount}
                pageSize={pagination.pageSize}
                pageSizeOptions={[10, 25, 50, 100]}
                startIndex={startIndex}
                endIndex={endIndex}
                canGoPrev={canGoPrev}
                canGoNext={canGoNext}
                onPageChange={goToPage}
                onPageSizeChange={setPageSize}
                onFirstPage={firstPage}
                onLastPage={lastPage}
                onPrevPage={prevPage}
                onNextPage={nextPage}
              />
            </div>
          </>
        )}
      </div>

      {/* Log Detail Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5" />
              Action Details
            </DialogTitle>
            <DialogDescription>
              {selectedLog?.agent_name} - {selectedLog?.action_type}
            </DialogDescription>
          </DialogHeader>

          {detailsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            selectedLog && (
              <div className="space-y-4 overflow-y-auto max-h-[60vh]">
                {/* Summary stats */}
                <div className={`grid gap-3 ${showCosts ? "grid-cols-4" : "grid-cols-3"}`}>
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                      <Clock className="h-3 w-3" />
                      Duration
                    </div>
                    <div className="font-semibold">
                      {formatDuration(selectedLog.execution_time_ms)}
                    </div>
                  </div>
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                      <Zap className="h-3 w-3" />
                      Tokens
                    </div>
                    <div className="font-semibold">
                      {selectedLog.total_tokens.toLocaleString()}
                    </div>
                  </div>
                  {showCosts && (
                    <div className="bg-muted/50 p-3 rounded-lg">
                      <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                        <Coins className="h-3 w-3" />
                        Cost
                      </div>
                      <div className="font-semibold">
                        ${selectedLog.cost_estimate.toFixed(4)}
                      </div>
                    </div>
                  )}
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <div className="text-muted-foreground text-xs mb-1">
                      Status
                    </div>
                    {getStatusBadge(selectedLog.status)}
                  </div>
                </div>

                {/* Basic info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Entity ID
                    </Label>
                    <p className="font-mono text-xs bg-muted p-2 rounded mt-1 break-all">
                      {selectedLog.entity_id}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Entity Type
                    </Label>
                    <p className="mt-1">
                      <Badge variant="outline">{selectedLog.entity_type}</Badge>
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Service
                    </Label>
                    <p className="mt-1">{selectedLog.service_name}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Timestamp
                    </Label>
                    <p className="mt-1">
                      {new Date(
                        selectedLog.timestamp || selectedLog.created_at,
                      ).toLocaleString()}
                    </p>
                  </div>
                  {selectedLog.workflow_execution_id && (
                    <div className="col-span-2">
                      <Label className="text-xs text-muted-foreground">
                        Workflow Execution ID
                      </Label>
                      <p className="font-mono text-xs bg-muted p-2 rounded mt-1 break-all">
                        {selectedLog.workflow_execution_id}
                      </p>
                    </div>
                  )}
                </div>

                {/* Full details JSON */}
                {logDetails && (
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Full Details
                    </Label>
                    <pre className="bg-muted/50 p-3 rounded text-xs overflow-x-auto mt-1 max-h-64 font-mono">
                      {JSON.stringify(logDetails, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
